import {useMemo} from "react";
import {BarElement, CategoryScale, Chart, LinearScale, Tooltip} from "chart.js";
import {useThemeMode} from "../../../core/style/theme-mode";

// chart.js v3 ağaç-sallamalı: yalnız kullanılan parçalar kaydedilir.
Chart.register(CategoryScale, LinearScale, BarElement, Tooltip);

const readVar = (styles, name, fallback) => (styles.getPropertyValue(name) || '').trim() || fallback;

/**
 * Grafik renkleri brand.css token'larından okunur. Canvas CSS değişkeni çözemediği için değerler
 * hesaplanmış stilden alınır ve tema değişince yeniden okunur (useThemeMode yeniden render ettirir).
 */
export function useChartTheme() {
    const mode = useThemeMode();

    return useMemo(() => {
        const styles = getComputedStyle(document.documentElement);
        return {
            mode,
            series: [
                readVar(styles, '--chart-1', '#2A78D6'),
                readVar(styles, '--chart-2', '#EB6834'),
                readVar(styles, '--chart-3', '#1BAF7A'),
                readVar(styles, '--chart-4', '#EDA100'),
            ],
            grid: readVar(styles, '--chart-grid', '#E6E9EC'),
            text: readVar(styles, '--text-2', '#5D6D7E'),
            muted: readVar(styles, '--text-3', '#8794A1'),
            surface: readVar(styles, '--surface', '#FFFFFF'),
            font: getComputedStyle(document.body).fontFamily,
        };
    }, [mode]);
}

/** Tek serili çubuk grafiklerin ortak ayarları: ince çubuk, yuvarlak uç, silik ızgara, hover ipucu. */
export function barOptions(theme, {horizontal = false, tooltipTitle} = {}) {
    const valueAxis = {
        beginAtZero: true,
        grid: {color: theme.grid, drawBorder: false},
        ticks: {color: theme.muted, precision: 0, font: {family: theme.font, size: 11}},
    };
    const categoryAxis = {
        grid: {display: false, drawBorder: false},
        ticks: {color: theme.text, font: {family: theme.font, size: 11}, autoSkip: true, maxRotation: 0},
    };

    return {
        indexAxis: horizontal ? 'y' : 'x',
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: {mode: 'index', intersect: false},
        scales: horizontal ? {x: valueAxis, y: categoryAxis} : {x: categoryAxis, y: valueAxis},
        plugins: {
            legend: {display: false},
            tooltip: {
                displayColors: false,
                titleFont: {family: theme.font},
                bodyFont: {family: theme.font},
                callbacks: tooltipTitle ? {title: tooltipTitle} : {},
            },
        },
    };
}

/**
 * slot: tek sayı → tüm çubuklar o seri renginde; dizi → çubuk başına renk (kategori kırılımı).
 * Dizide null olan çubuk nötr griye düşer ("diğer" kategoriler).
 */
export function barDataset(theme, data, slot = 0) {
    const color = Array.isArray(slot)
        ? slot.map(x => (x == null ? theme.muted : theme.series[x]))
        : theme.series[slot];

    return {
        data,
        backgroundColor: color,
        hoverBackgroundColor: color,
        borderRadius: 4,
        borderSkipped: 'start',
        maxBarThickness: 28,
    };
}
